import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Button from '../../components/Button'
import { Pages } from '../../Pages'
import personApi, {
    LinkedInWorkHistoryOption,
} from '../../api/person/PersonApi'
import LoadingSpinner from '../common/loading/LoadingSpinner'
import { useAuth } from '../../auth/AuthContext'
import { CsPersonSearchResultDto } from '../../api/person/models/searchCs/SearchPersonsCsDtos'
import ProfilePic from '../common/person/ProfilePic'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
import { completeLinkedInSignup } from './helpers/completeLinkedInSignup'
import tenantApi from '../../api/tenant/TenantApi'
import { normalizeLinkedInProfileUrl } from '../../utils/linkedin'

const PERSON_MATCH_RESULTS_LIMIT = 10
const INVALID_LINKEDIN_URL_MESSAGE =
    'Please enter a full LinkedIn profile URL like https://www.linkedin.com/in/your-name.'

type Stage = 'input' | 'enter-name' | 'select-match' | 'select-job'

type ResumeState = {
    resumeStage?: 'select-job'
    linkedInUrl?: string
    matchedPerson?: CsPersonSearchResultDto
    jobOptions?: LinkedInWorkHistoryOption[]
}

export default function SignUpLinkedIn(): React.JSX.Element {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, loading, refresh, logout } = useAuth()
    const [checkingExistingProfile, setCheckingExistingProfile] = useState(true)

    const initialLinkedInUrl = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return params.get('linkedinUrl') ?? ''
    }, [location.search])

    const [linkedinUrl, setLinkedinUrl] = useState(initialLinkedInUrl)
    const [status, setStatus] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [checking, setChecking] = useState(false)
    const [stage, setStage] = useState<Stage>('input')
    const [csResults, setCsResults] = useState<CsPersonSearchResultDto[]>([])
    const [selectedCsPersonId, setSelectedCsPersonId] = useState<string | null>(
        null,
    )
    const [jobOptions, setJobOptions] = useState<LinkedInWorkHistoryOption[]>(
        [],
    )
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
    const [pendingPerson, setPendingPerson] =
        useState<CsPersonSearchResultDto | null>(null)
    const [pendingLinkedinUrl, setPendingLinkedinUrl] = useState('')
    const [nameQuery, setNameQuery] = useState('')

    const pendingPersonLabel = useMemo(() => {
        if (!pendingPerson) {
            return ''
        }

        const label = [
            pendingPerson.firstName,
            pendingPerson.middleName,
            pendingPerson.lastName,
        ]
            .filter(Boolean)
            .join(' ')

        return label || 'Unknown name'
    }, [pendingPerson])

    useEffect(() => {
        if (loading) {
            return
        }

        if (!user) {
            setCheckingExistingProfile(false)
            navigate(Pages.Login)
            return
        }

        let cancelled = false

        const verifyLinkedProfile = async () => {
            try {
                setCheckingExistingProfile(true)
                // TODO : 151 - This throws an error, we need to find a cleaner approach to check if the user has completed their profile
                const response = await tenantApi.getLoggedInUser()
                if (cancelled) {
                    return
                }

                if (response.user.personId) {
                    navigate(Pages.Roles, { replace: true })
                    return
                }
            } catch (error) {
                if (!cancelled) {
                    console.error(
                        'Failed to verify LinkedIn signup completeness',
                        error,
                    )
                }
            } finally {
                if (!cancelled) {
                    setCheckingExistingProfile(false)
                }
            }
        }

        void verifyLinkedProfile()

        return () => {
            cancelled = true
        }
    }, [loading, user, navigate])

    useEffect(() => {
        const resumeState = location.state as ResumeState | null
        if (
            resumeState?.resumeStage === 'select-job' &&
            resumeState.matchedPerson
        ) {
            const options = resumeState.jobOptions ?? []
            setLinkedinUrl(resumeState.linkedInUrl ?? '')
            setPendingPerson(resumeState.matchedPerson)
            setPendingLinkedinUrl(resumeState.linkedInUrl ?? '')
            setSelectedCsPersonId(resumeState.matchedPerson.id)
            setJobOptions(options)
            setSelectedJobId(options[0]?.workHistoryId ?? null)
            setCsResults([])
            setStage('select-job')
            setStatus(null)
            navigate(location.pathname, { replace: true, state: null })
        }
    }, [location.pathname, location.state, navigate])

    const finalizeSignup = useCallback(
        async ({
            linkedinUrl: url,
            person,
            workHistoryId,
            onNeedsWorkHistory,
            onCompleted,
        }: {
            linkedinUrl: string
            person: CsPersonSearchResultDto | null
            workHistoryId?: string
            onNeedsWorkHistory?: (
                options: LinkedInWorkHistoryOption[],
                selectedPerson: CsPersonSearchResultDto,
                selectedUrl: string,
            ) => void
            onCompleted?: () => void
        }) => {
            try {
                setSaving(true)
                setStatus(null)

                const normalizedUrl = normalizeLinkedInProfileUrl(url)

                if (!normalizedUrl) {
                    setStatus(INVALID_LINKEDIN_URL_MESSAGE)
                    return
                }

                const response = await completeLinkedInSignup({
                    linkedinUrl: normalizedUrl,
                    person,
                    workHistoryId,
                })

                if (response.status === 'needs_work_history_selection') {
                    if (!person) {
                        throw new Error(
                            'Unable to determine current job options without a selected person.',
                        )
                    }

                    if (onNeedsWorkHistory) {
                        onNeedsWorkHistory(
                            response.workHistoryOptions,
                            person,
                            normalizedUrl,
                        )
                    } else {
                        setJobOptions(response.workHistoryOptions)
                        setSelectedJobId(
                            response.workHistoryOptions[0]?.workHistoryId ??
                                null,
                        )
                        setPendingPerson(person)
                        setPendingLinkedinUrl(normalizedUrl)
                        setSelectedCsPersonId(person.id)
                        setStage('select-job')
                    }
                    return
                }

                await refresh()

                if (onCompleted) {
                    onCompleted()
                } else {
                    navigate(Pages.Roles, { replace: true })
                }
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to save LinkedIn selection. Please try again.'
                setStatus(message)
            } finally {
                setSaving(false)
            }
        },
        [navigate, refresh],
    )

    const handleLookup = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            const trimmed = linkedinUrl.trim()
            if (!trimmed) {
                setStatus('Please add your LinkedIn URL to continue.')
                return
            }

            const normalized = normalizeLinkedInProfileUrl(trimmed)

            if (!normalized) {
                setStatus(INVALID_LINKEDIN_URL_MESSAGE)
                return
            }

            setStatus(null)
            setLinkedinUrl(normalized)
            setCsResults([])
            setSelectedCsPersonId(null)
            setJobOptions([])
            setSelectedJobId(null)
            setPendingPerson(null)
            setPendingLinkedinUrl('')
            setStage('input')

            try {
                setChecking(true)
                const response = await personApi.searchPersonsCsByLinkedInUrl(
                    normalized,
                    1,
                    PERSON_MATCH_RESULTS_LIMIT,
                )

                const results = response.results ?? []

                if (results.length === 0) {
                    setStage('enter-name')
                    setNameQuery('')
                    return
                }

                if (results.length === 1) {
                    navigate(Pages.SignupLinkedInConfirm, {
                        replace: true,
                        state: {
                            linkedInUrl: normalized,
                            matchedPerson: results[0],
                        },
                    })
                    return
                }

                setCsResults(results.slice(0, PERSON_MATCH_RESULTS_LIMIT))
                setStage('select-match')
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to search cold storage. Please try again.'
                setStatus(message)
            } finally {
                setChecking(false)
            }
        },
        [linkedinUrl, navigate],
    )

    const handleSelectMatch = useCallback(
        async (result: CsPersonSearchResultDto) => {
            if (saving) {
                return
            }

            const normalized = normalizeLinkedInProfileUrl(linkedinUrl)

            if (!normalized) {
                setStatus(INVALID_LINKEDIN_URL_MESSAGE)
                return
            }

            setSelectedCsPersonId(result.id)
            setStatus(null)

            await finalizeSignup({
                linkedinUrl: normalized,
                person: result,
            })
        },
        [finalizeSignup, linkedinUrl, saving],
    )

    const handleUseDifferentUrl = useCallback(() => {
        setStage('input')
        setStatus(null)
        setCsResults([])
        setSelectedCsPersonId(null)
        setJobOptions([])
        setSelectedJobId(null)
        setPendingPerson(null)
        setPendingLinkedinUrl('')
        setNameQuery('')
    }, [])

    const handleSelectJob = useCallback(
        async (workHistoryId: string) => {
            if (saving) {
                return
            }

            if (!pendingPerson) {
                setStatus('Please choose your LinkedIn profile again.')
                setStage('select-match')
                return
            }

            if (!pendingLinkedinUrl) {
                setStatus('Please choose your LinkedIn profile again.')
                handleUseDifferentUrl()
                return
            }

            setSelectedJobId(workHistoryId)
            setStatus(null)

            await finalizeSignup({
                linkedinUrl: pendingLinkedinUrl,
                person: pendingPerson,
                workHistoryId,
            })
        },
        [
            finalizeSignup,
            handleUseDifferentUrl,
            pendingLinkedinUrl,
            pendingPerson,
            saving,
            setStage,
        ],
    )

    const handleGoBackOneStep = useCallback(() => {
        if (saving) {
            return
        }

        if (csResults.length > 1) {
            setStatus(null)
            setJobOptions([])
            setSelectedJobId(null)
            setPendingPerson(null)
            setPendingLinkedinUrl('')
            setStage('select-match')
        } else {
            handleUseDifferentUrl()
        }
    }, [csResults.length, handleUseDifferentUrl, saving])

    const handleBack = useCallback(() => {
        logout()
            .catch(() => undefined)
            .finally(() => {
                navigate(Pages.Login, { replace: true })
            })
    }, [logout, navigate])

    const handleNameSearch = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            const trimmed = nameQuery.trim()
            const parts = trimmed.split(/\s+/).filter(Boolean)

            if (parts.length < 2) {
                setStatus('Please enter your full name (first and last).')
                return
            }

            try {
                setChecking(true)
                setStatus(null)
                const response = await personApi.searchPersonsCsByFullName(
                    trimmed,
                    {
                        pageNumber: 1,
                        perPage: PERSON_MATCH_RESULTS_LIMIT,
                    },
                )

                const results = response.results ?? []

                if (results.length === 0) {
                    setStatus(
                        'No matches found. Please adjust the name and search again.',
                    )
                    setCsResults([])
                    setSelectedCsPersonId(null)
                    return
                }

                if (results.length === 1) {
                    const normalizedLinkedIn =
                        normalizeLinkedInProfileUrl(linkedinUrl)

                    navigate(Pages.SignupLinkedInConfirm, {
                        replace: true,
                        state: {
                            linkedInUrl:
                                normalizedLinkedIn ?? linkedinUrl.trim(),
                            matchedPerson: results[0],
                        },
                    })
                    return
                }

                setCsResults(results.slice(0, PERSON_MATCH_RESULTS_LIMIT))
                setSelectedCsPersonId(null)
                setStage('select-match')
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to search cold storage. Please try again.'
                setStatus(message)
            } finally {
                setChecking(false)
            }
        },
        [linkedinUrl, nameQuery, navigate],
    )

    if (loading || checkingExistingProfile || !user) {
        return <LoadingSpinner />
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
            <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-8 space-y-6 text-center">
                <div className="space-y-3">
                    <img
                        src="/Contxt_Logo_Full_BLK_CROPPED.png"
                        className="w-40 mx-auto mb-8"
                        alt="Contxt"
                    />
                    <h2 className="text-2xl font-bold">
                        {stage === 'input'
                            ? 'Add your LinkedIn URL'
                            : stage === 'select-job'
                              ? 'Confirm your current role'
                              : 'Confirm your LinkedIn profile'}
                    </h2>
                </div>

                {stage === 'input' && (
                    <>
                        <div className="space-y-2 text-sm text-gray-600">
                            <p>
                                We&apos;ll use your LinkedIn profile to help
                                build your Contxt network.
                            </p>
                            <a
                                href="https://www.linkedin.com/in/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
                            >
                                linkedin.com/in
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                    className="text-xs"
                                />
                            </a>
                        </div>
                        <form className="space-y-4" onSubmit={handleLookup}>
                            <input
                                type="text"
                                inputMode="url"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="https://www.linkedin.com/in/your-profile"
                                value={linkedinUrl}
                                onChange={(event) => {
                                    setLinkedinUrl(event.target.value)
                                    setStatus(null)
                                }}
                                required
                                disabled={saving || checking}
                            />
                            <Button
                                style="primary"
                                type="submit"
                                classes="w-full"
                                enabled={!saving && !checking}
                            >
                                Continue
                            </Button>
                        </form>
                    </>
                )}

                {stage === 'enter-name' && (
                    <>
                        <div className="space-y-2 text-sm text-gray-600">
                            <p>
                                We couldn’t find a LinkedIn profile that matches
                                the URL you provided. Enter your full name to
                                search instead.
                            </p>
                        </div>
                        <form className="space-y-4" onSubmit={handleNameSearch}>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="First and last name"
                                value={nameQuery}
                                onChange={(event) => {
                                    setNameQuery(event.target.value)
                                    setStatus(null)
                                }}
                                disabled={saving || checking}
                            />
                            <div className="space-y-2">
                                <Button
                                    style="primary"
                                    type="submit"
                                    classes="w-full"
                                    enabled={!saving && !checking}
                                >
                                    Search by name
                                </Button>
                                <Button
                                    style="secondary"
                                    type="button"
                                    classes="w-full"
                                    enabled={!saving && !checking}
                                    onClick={handleUseDifferentUrl}
                                >
                                    Try a different LinkedIn URL
                                </Button>
                            </div>
                        </form>
                    </>
                )}

                {stage === 'select-match' && (
                    <div className="space-y-4 text-left">
                        <p className="text-sm text-gray-600">
                            We found multiple matches. Please choose the one
                            that is you.
                        </p>
                        <div className="space-y-3">
                            {csResults.map((result) => {
                                const label = [
                                    result.firstName,
                                    result.middleName,
                                    result.lastName,
                                ]
                                    .filter(Boolean)
                                    .join(' ')
                                const isSelected =
                                    selectedCsPersonId === result.id

                                return (
                                    <button
                                        key={result.id}
                                        type="button"
                                        onClick={() => {
                                            void handleSelectMatch(result)
                                        }}
                                        disabled={saving}
                                        className={`w-full rounded-xl border text-left overflow-hidden transition focus:outline-none focus:ring-2 focus:ring-ctxt-blue focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                                            isSelected
                                                ? 'border-ctxt-blue/80 bg-blue-50/40 shadow-md'
                                                : 'border-gray-200 hover:border-ctxt-blue/40 hover:bg-gray-50 shadow-sm'
                                        }`}
                                    >
                                        <div className="flex items-center gap-4 px-4 py-3">
                                            <div className="flex-shrink-0">
                                                <ProfilePic
                                                    src={
                                                        result.linkedInProfilePicUrl
                                                    }
                                                    size={48}
                                                    alt={
                                                        label ||
                                                        'LinkedIn profile picture'
                                                    }
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0 text-left space-y-1">
                                                <p className="text-base font-semibold text-gray-900 truncate">
                                                    {label || 'Unknown name'}
                                                </p>
                                                {result.headline && (
                                                    <p className="text-sm text-gray-500 line-clamp-2">
                                                        {result.headline}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                        <div className="text-center">
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleUseDifferentUrl}
                            >
                                That's not me
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'select-job' && (
                    <div className="space-y-4 text-left">
                        <p className="text-sm text-gray-600">
                            {pendingPersonLabel
                                ? `We found multiple current roles for ${pendingPersonLabel}. Choose the one to link your account to.`
                                : 'We found multiple current roles. Choose the one to link your account to.'}
                        </p>
                        <div className="space-y-3">
                            {jobOptions.length === 0 && (
                                <p className="text-sm text-gray-500">
                                    No current roles were returned. Please go
                                    back and try again.
                                </p>
                            )}
                            {jobOptions.map((option) => {
                                const isSelected =
                                    selectedJobId === option.workHistoryId
                                const jobTitle =
                                    option.jobTitle || 'Current role'

                                return (
                                    <button
                                        key={option.workHistoryId}
                                        type="button"
                                        onClick={() => {
                                            void handleSelectJob(
                                                option.workHistoryId,
                                            )
                                        }}
                                        disabled={saving}
                                        className={`w-full rounded-xl border text-left overflow-hidden transition focus:outline-none focus:ring-2 focus:ring-ctxt-blue focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                                            isSelected
                                                ? 'border-ctxt-blue/80 bg-blue-50/40 shadow-md'
                                                : 'border-gray-200 hover:border-ctxt-blue/40 hover:bg-gray-50 shadow-sm'
                                        }`}
                                    >
                                        <div className="flex items-start gap-4 px-4 py-3">
                                            <div className="flex-1 min-w-0 space-y-1">
                                                <p className="text-base font-semibold text-gray-900 truncate">
                                                    {jobTitle}
                                                </p>
                                                {option.companyName && (
                                                    <p className="text-sm text-gray-500 truncate">
                                                        {option.companyName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                        <div className="text-center">
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleGoBackOneStep}
                                disabled={saving}
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                )}

                {status && (
                    <p className={`text-sm whitespace-pre-wrap text-red-600`}>
                        {status}
                    </p>
                )}

                {stage === 'input' && (
                    <button
                        type="button"
                        className="text-sm text-blue-600 hover:underline"
                        onClick={handleBack}
                        disabled={saving || checking}
                    >
                        Back to login
                    </button>
                )}
            </div>
        </div>
    )
}
